// src/components/PaymentPlanModal.jsx
// Payment Plan Generator — configure installment schedule, preview, save to Firestore, send via SMS
import React, { useState, useMemo } from 'react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatCurrency } from '../utils/formatters';
import { generateMessageId } from '../utils/generateId';
import { createPortalAccessToken } from '../utils/compliance';
import { sendTenantSms } from '../utils/outreach';

// ── Helpers ────────────────────────────────────────────────────────────────────
// Add N months to a date (handles month rollover)
function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

// Format a date as "Month D, YYYY"
function fmtDate(date) {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Build installment schedule from balance + count starting at a given date
function buildSchedule(balance, count, startDate) {
  const perInstallment = parseFloat((balance / count).toFixed(2));
  // Last installment absorbs rounding difference
  const schedule = [];
  let remaining = balance;
  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1;
    const amount = isLast ? parseFloat(remaining.toFixed(2)) : perInstallment;
    schedule.push({
      installment: i + 1,
      amount,
      dueDate: addMonths(startDate, i).toISOString().slice(0, 10),
      status: 'pending',
    });
    remaining -= perInstallment;
  }
  return schedule;
}

// Build the SMS text for the plan. The actual secure payment link is appended
// server-side so preview text stays readable in local environments.
function buildSmsText(tenantName, balance, schedule, lang = 'en', includeLinkNote = false) {
  const first = schedule[0];
  const perInstallment = first.amount;
  const count = schedule.length;
  const linkLine = includeLinkNote ? ' A secure payment link will be included.' : '';

  if (lang === 'es') {
    return (
      `Hola ${tenantName}, hemos creado un plan de pago para su saldo de ${formatCurrency(balance)}: ` +
      `${count} pagos de ${formatCurrency(perInstallment)}. ` +
      `Primer pago: ${fmtDate(first.dueDate)}.${linkLine} ` +
      `Responda STOP para cancelar mensajes.`
    );
  }
  return (
    `Hi ${tenantName}, we've set up a payment plan for your ${formatCurrency(balance)} balance: ` +
    `${count} payments of ${formatCurrency(perInstallment)}. ` +
    `First payment due: ${fmtDate(first.dueDate)}.${linkLine} ` +
    `Reply STOP to opt out.`
  );
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function PaymentPlanModal({
  tenant,
  propertyId,
  maxInstallments = 3,
  minPaymentAmount = 100,
  onClose,
  onSaved,
}) {
  const balance = tenant?.currentBalance || 0;

  // Clamp installments to what the balance actually supports
  const maxAffordable = Math.min(
    maxInstallments,
    Math.floor(balance / Math.max(minPaymentAmount, 1))
  );
  const maxAllowed = Math.max(maxAffordable, 1);

  const [numInstallments, setNumInstallments] = useState(Math.min(3, maxAllowed));
  const [startDate, setStartDate] = useState(() => {
    // Default to 1st of next month
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 1);
    return d.toISOString().slice(0, 10);
  });
  const [lang, setLang] = useState(tenant?.preferredLanguage || 'en');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Stable messageId for this plan notification — used to attribute payments back to this SMS
  const [planMessageId] = useState(() => generateMessageId());
  const [portalAccessToken] = useState(() => tenant?.portalAccessToken || createPortalAccessToken());

  // Recompute schedule whenever inputs change
  const schedule = useMemo(
    () => buildSchedule(balance, numInstallments, startDate),
    [balance, numInstallments, startDate]
  );

  const smsPreview = useMemo(
    () => buildSmsText(tenant?.name || 'Tenant', balance, schedule, lang, true),
    [tenant?.name, balance, schedule, lang]
  );

  // ── Save plan to Firestore (no SMS) ──────────────────────────────────────────
  const handleSave = async () => {
    if (!propertyId || !tenant?.id) return;
    setSaving(true);
    setError('');
    try {
      const tenantRef = doc(db, 'properties', propertyId, 'tenants', tenant.id);
      await updateDoc(tenantRef, {
        paymentPlan: {
          installments: schedule,
          totalBalance: balance,
          status: 'active',
          createdAt: new Date().toISOString(),
          lang,
        },
        portalAccessToken,
        // Log plan creation in outreach log (with messageId for attribution)
        outreachLog: arrayUnion({
          channel: 'payment_plan',
          status: 'created',
          messageId: planMessageId,
          message: `Payment plan created: ${numInstallments}x ${formatCurrency(schedule[0].amount)}, starting ${fmtDate(startDate)}.`,
          date: new Date().toISOString(),
          installments: numInstallments,
          amount: schedule[0].amount,
        }),
      });
      setSaved(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Save plan + send SMS via Firebase Function ────────────────────────────────
  const handleSendSMS = async () => {
    if (!tenant?.phone) {
      setError('No phone number on file for this tenant.');
      return;
    }
    setSending(true);
    setError('');
    try {
      // First save the plan to Firestore
      const tenantRef = doc(db, 'properties', propertyId, 'tenants', tenant.id);
      await updateDoc(tenantRef, {
        paymentPlan: {
          installments: schedule,
          totalBalance: balance,
          status: 'active',
          createdAt: new Date().toISOString(),
          lang,
        },
        portalAccessToken,
        outreachLog: arrayUnion({
          channel: 'payment_plan',
          status: 'created',
          messageId: planMessageId,
          message: `Payment plan prepared: ${numInstallments}x ${formatCurrency(schedule[0].amount)}, starting ${fmtDate(startDate)}.`,
          date: new Date().toISOString(),
          installments: numInstallments,
          amount: schedule[0].amount,
        }),
      });

      // Then send the SMS via the shared live SMS helper
      try {
        await sendTenantSms({
          tenant,
          propertyId,
          message: smsPreview,
          templateKey: 'paymentPlan',
          lang,
          includePaymentLink: true,
        });
      } catch (smsErr) {
        // SMS send failed but plan was saved — surface a warning, not a hard error
        console.warn('SMS send failed (plan was saved):', smsErr.message);
        setError('Plan saved. SMS failed to send — check your Twilio config in Settings.');
        setSending(false);
        setSaved(true);
        if (onSaved) onSaved();
        return;
      }

      setSaved(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError('Failed: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  if (!tenant) return null;

  const isActive = saving || sending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="font-semibold text-gray-900">💳 Payment Plan</h2>
            <p className="text-sm text-gray-500 mt-0.5">{tenant.name} · Unit {tenant.unit}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        <div className="p-6 space-y-6">

          {/* Balance summary */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-red-500 font-semibold uppercase tracking-wide">Outstanding Balance</p>
              <p className="text-2xl font-bold text-red-600 mt-0.5">{formatCurrency(balance)}</p>
            </div>
            {tenant.currentDaysLate > 0 && (
              <span className="text-sm text-red-500">{tenant.currentDaysLate} days overdue</span>
            )}
          </div>

          {/* Config: installments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Installments
              <span className="text-gray-400 font-normal ml-1">(max {maxAllowed})</span>
            </label>
            <div className="flex gap-2 flex-wrap">
              {[2, 3, 4, 6].filter(n => n <= maxAllowed).map(n => (
                <button
                  key={n}
                  onClick={() => setNumInstallments(n)}
                  type="button"
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${
                    numInstallments === n
                      ? 'bg-tenafi-green text-white border-tenafi-green'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-tenafi-green hover:text-tenafi-green'
                  }`}
                >
                  {n} payments
                </button>
              ))}
            </div>
          </div>

          {/* Config: start date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">First Payment Due Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
            />
          </div>

          {/* Installment schedule preview */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Installment Schedule</p>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              {schedule.map((inst, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-4 py-3 text-sm ${
                    i < schedule.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-tenafi-light text-tenafi-green text-xs font-bold flex items-center justify-center">
                      {inst.installment}
                    </span>
                    <span className="text-gray-500">{fmtDate(inst.dueDate)}</span>
                  </div>
                  <span className="font-semibold text-gray-900">{formatCurrency(inst.amount)}</span>
                </div>
              ))}
              {/* Total row */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-semibold">
                <span className="text-gray-500">Total</span>
                <span className="text-gray-900">{formatCurrency(balance)}</span>
              </div>
            </div>
          </div>

          {/* Language toggle */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">SMS Language</label>
            <div className="flex gap-2">
              <button onClick={() => setLang('en')} type="button"
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  lang === 'en' ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                🇺🇸 English
              </button>
              <button onClick={() => setLang('es')} type="button"
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  lang === 'es' ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                🇲🇽 Español
              </button>
            </div>
          </div>

          {/* SMS preview */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">📱 SMS Preview</p>
            <p className="text-sm text-gray-700 leading-relaxed">{smsPreview}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Success */}
          {saved && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 font-medium">
              ✓ Payment plan saved successfully!
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 flex flex-wrap items-center justify-end gap-3">
          <button onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800">
            {saved ? 'Close' : 'Cancel'}
          </button>
          {!saved && (
            <>
              <button
                onClick={handleSave}
                disabled={isActive || balance <= 0}
                className="px-4 py-2.5 border border-tenafi-green text-tenafi-green rounded-xl text-sm font-semibold hover:bg-tenafi-light transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Plan'}
              </button>
              <button
                onClick={handleSendSMS}
                disabled={isActive || !tenant.phone || balance <= 0}
                className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {sending ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                ) : (
                  '📱 Save + Send SMS'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
